import type { RouteRecordRaw } from 'vue-router';
import 'vue-router';
import { FRONTEND_BUSINESS_RULES, SYSTEM_CONSTANTS } from '@/config/appConfig';
import { useAuthStore } from '@/stores/auth';
import type { RoleContext } from '@/types/database';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    allowedRoles?: string[];
    roleContext?: RoleContext;
  }
}

const { USER_ROLES } = SYSTEM_CONSTANTS;

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    component: () => import('../pages/LoginPage.vue'),
  },
  {
    path: '/logout',
    beforeEnter: async (_to, _from, next) => {
      const authStore = useAuthStore();
      await authStore.logout();
      next('/login');
    },
    // This route doesn't need a component because it just redirects.
    component: { template: '<div>Logging out...</div>' },
  },
  {
    path: '/kiosk',
    component: () => import('../pages/KioskPage.vue'),
  },
  {
    // Wall-iPad 打卡 station (onsite staff punch). Top-level (like /kiosk) so it
    // renders fullscreen without the MainLayout sidebar — a clean kiosk surface.
    path: '/kiosk/clock',
    component: () => import('../pages/kiosk/KioskClockPage.vue'),
    meta: {
      requiresAuth: true,
      allowedRoles: [USER_ROLES.KIOSK, USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
      roleContext: 'kiosk',
    },
  },
  {
    path: '/link-line',
    component: () => import('../pages/LinkLinePage.vue'),
  },
  {
    // 紙本憑證送件單（列印）。刻意放在最上層（同 /kiosk）而不是 MainLayout 底下：
    // 列印的是一張交給事務所簽收的 A4，不該帶側欄、頂欄與漸層背景。
    // 從紙本憑證頁的「交件並列印」／「重印」開新分頁進來：?batch={id} ＝ 已交件的那一份，
    // 「交件並列印」另帶 &print=1（畫好後自動叫出列印視窗一次）。預覽只在交件視窗裡（D7）。
    path: '/payment/paper-receipts/print',
    component: () => import('../pages/payment/PaperHandoverPrintPage.vue'),
    meta: {
      requiresAuth: true,
      allowedRoles: [USER_ROLES.GENERAL_AFFAIRS, USER_ROLES.SUPERVISOR, USER_ROLES.TREASURY],
      roleContext: 'payment',
    },
  },
  {
    path: '/',
    component: () => import('../layouts/MainLayout.vue'),
    children: [
      { path: '', redirect: '/home' },
      {
        path: 'home',
        component: () => import('../pages/HomePage.vue'),
        meta: { requiresAuth: true },
      },
      // ─── Admin Pages (roleContext: 'admin') ────────────────────────────────
      {
        path: 'admin/upload',
        component: () => import('../pages/admin/SignInPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'admin',
        },
      },
      {
        path: 'admin/schedule',
        component: () => import('../pages/admin/SignInPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'admin',
        },
      },
      {
        path: 'admin/pending',
        component: () => import('../pages/admin/SignInPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'admin',
        },
      },
      {
        path: 'admin/discrepancies',
        component: () => import('../pages/admin/SignInPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'admin',
        },
      },
      {
        path: 'admin/sign-in',
        component: () => import('../pages/admin/SignInPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'admin',
        },
      },
      {
        // 常態班更名 (班別識別統一 — class-identity canonicalization: class rename and/or
        // permanent-teacher change) — SUPERVISOR ONLY. Menu label = UI_STRINGS.CLASS_IDENTITY.PAGE_TITLE
        // (useNavigationMenu); route meta has no title field.
        path: 'admin/class-identity',
        component: () => import('../pages/admin/ClassIdentityMappingPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.SUPERVISOR],
          roleContext: 'admin',
        },
      },
      {
        path: 'admin/tasks',
        component: () => import('../pages/admin/SignInPage.vue'), // Temporary - reuse SignIn page
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'admin',
        },
      },
      {
        path: 'admin/feedback',
        component: () => import('../pages/admin/FeedbackPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.TEACHER, USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'admin',
        },
      },
      {
        path: 'admin/time-slots',
        name: 'TimeSlotPage',
        component: () => import('../pages/admin/TimeSlotPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'admin',
        },
      },
      {
        path: 'admin/message-composer',
        name: 'MessageComposerPage',
        component: () => import('../pages/admin/MessageComposerPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'admin',
        },
      },
      {
        path: 'admin/students',
        component: () => import('../pages/admin/SignInPage.vue'), // Temporary - reuse SignIn page
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'admin',
        },
      },
      {
        path: 'admin/data',
        component: () => import('../pages/admin/SignInPage.vue'), // Temporary - reuse SignIn page
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'admin',
        },
      },
      {
        path: 'admin/settings',
        component: () => import('../pages/admin/ConfigPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.SUPERVISOR, USER_ROLES.TREASURY],
          roleContext: 'admin',
        },
      },
      {
        path: 'admin/instructions',
        component: () => import('../pages/admin/InstructionsManagementPage.vue'),
        meta: { requiresAuth: true, allowedRoles: [USER_ROLES.SUPERVISOR], roleContext: 'admin' },
      },
      {
        path: 'admin/instructions/:galleryId',
        component: () => import('../pages/admin/InstructionsManagementPage.vue'),
        meta: { requiresAuth: true, allowedRoles: [USER_ROLES.SUPERVISOR], roleContext: 'admin' },
      },
      // ─── Teacher Desktop Pages (roleContext: 'teacher-desktop') ─────────────
      // ─── /teacher/upcoming — DEACTIVATED 2026-05-05 ───────────────────────────
      // 瀏覽課程 page is no longer in use. Kept as a redirect to /teacher/mobile
      // (instead of removed) per product owner decision so any cached bookmarks /
      // deep links land somewhere usable instead of 404.
      //
      // The TeacherPage.vue component is STILL USED by /teacher/signin and
      // /teacher/completed below. Do NOT delete TeacherPage.vue or strip its
      // `currentView === 'upcoming'` code paths without product owner approval.
      //
      // Menu entry removed in useNavigationMenu.ts.
      // TEACHER role default landing changed in permissions.ts (was /teacher/upcoming).
      // To re-enable: restore the original route entry (component import +
      // roleContext/pageType meta), restore the menu entry, and revert the
      // landing-route change.
      {
        path: 'teacher/upcoming',
        redirect: '/teacher/mobile',
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.TEACHER],
        },
      },
      {
        path: 'teacher/signin',
        component: () => import('../pages/TeacherPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.TEACHER, USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'teacher-desktop',
        },
      },
      {
        path: 'teacher/completed',
        component: () => import('../pages/TeacherPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.TEACHER, USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'teacher-desktop',
        },
      },
      // ─── Tools (roleContext: 'tools') ───────────────────────────────────────
      {
        path: 'tools/countdown',
        component: () => import('../pages/admin/SyncedCountdownPage.vue'),
        meta: {
          requiresAuth: true,
          // 計時器 = TOOLS.TIMER_ROLES (the countdown CFs read the same list; 救生員
          // = same as teachers incl. takeover, PO P1 2026-09-16).
          allowedRoles: [...FRONTEND_BUSINESS_RULES.TOOLS.TIMER_ROLES],
          roleContext: 'tools',
        },
      },
      {
        path: 'tools/countdown/:id',
        component: () => import('../pages/admin/SyncedCountdownPage.vue'),
        meta: {
          requiresAuth: true,
          // 計時器 = TOOLS.TIMER_ROLES (the countdown CFs read the same list; 救生員
          // = same as teachers incl. takeover, PO P1 2026-09-16).
          allowedRoles: [...FRONTEND_BUSINESS_RULES.TOOLS.TIMER_ROLES],
          roleContext: 'tools',
        },
      },
      {
        path: 'tools/bf-calculator',
        component: () => import('../pages/tools/BfCalculatorPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'tools',
        },
      },
      {
        path: 'tools/draw-sequence',
        component: () => import('../pages/tools/DrawSequencePage.vue'),
        meta: {
          requiresAuth: true,
          // 抽籤順序 is local-only (no CF); 救生員 added with the timers (PO 2026-09-16).
          allowedRoles: [
            USER_ROLES.ADMIN,
            USER_ROLES.SUPERVISOR,
            USER_ROLES.TEACHER,
            USER_ROLES.KIOSK,
            USER_ROLES.LIFEGUARD,
          ],
          roleContext: 'tools',
        },
      },
      {
        path: 'admin/help',
        component: () => import('../pages/admin/AdminInstructionsPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR, USER_ROLES.KIOSK],
          roleContext: 'admin',
        },
      },
      // ─── Kiosk (roleContext: 'kiosk') ───────────────────────────────────────
      {
        path: 'kiosk/main',
        component: () => import('../pages/admin/KioskMainPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.KIOSK, USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'kiosk',
        },
      },
      // ─── Payment Module (roleContext: 'payment') ───────────────────────────
      // 救生員 file 請款 like 一般員工 (PO 2026-09-16) — the six requester routes below
      // admit them; 核准／出納／退費／設定 stay closed. These pages render in the desktop
      // MainLayout, so a 救生員 arriving from the phone home gets the sidebar, whose
      // 班級狀況 group is their way back to 首頁／上課名單.
      {
        path: 'payment/requests',
        component: () => import('../pages/payment/MyRequestsPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [
            USER_ROLES.ADMIN,
            USER_ROLES.SUPERVISOR,
            USER_ROLES.TEACHER,
            USER_ROLES.TREASURY,
            USER_ROLES.GENERAL_AFFAIRS,
            USER_ROLES.STAFF,
            USER_ROLES.WORK_STUDY,
            USER_ROLES.LIFEGUARD,
          ],
          roleContext: 'payment',
        },
      },
      {
        path: 'payment/payslips',
        component: () => import('../pages/payment/MyPayslipsPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [
            USER_ROLES.ADMIN,
            USER_ROLES.SUPERVISOR,
            USER_ROLES.TEACHER,
            USER_ROLES.TREASURY,
            USER_ROLES.GENERAL_AFFAIRS,
            USER_ROLES.STAFF,
            USER_ROLES.LIFEGUARD,
            USER_ROLES.WORK_STUDY,
          ],
          roleContext: 'payment',
        },
      },
      {
        path: 'payment/profile',
        component: () => import('../pages/payment/PayeeProfilePage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [
            USER_ROLES.ADMIN,
            USER_ROLES.SUPERVISOR,
            USER_ROLES.TEACHER,
            USER_ROLES.TREASURY,
            USER_ROLES.STAFF,
            USER_ROLES.WORK_STUDY,
            USER_ROLES.LIFEGUARD,
          ],
          roleContext: 'payment',
        },
      },
      {
        path: 'payment/requests/new',
        component: () => import('../pages/payment/RequestFormPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [
            USER_ROLES.ADMIN,
            USER_ROLES.SUPERVISOR,
            USER_ROLES.TEACHER,
            USER_ROLES.TREASURY,
            USER_ROLES.STAFF,
            USER_ROLES.WORK_STUDY,
            USER_ROLES.LIFEGUARD,
          ],
          roleContext: 'payment',
        },
      },
      {
        path: 'payment/requests/:id/edit',
        component: () => import('../pages/payment/RequestFormPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [
            USER_ROLES.ADMIN,
            USER_ROLES.SUPERVISOR,
            USER_ROLES.TEACHER,
            USER_ROLES.TREASURY,
            USER_ROLES.STAFF,
            USER_ROLES.WORK_STUDY,
            USER_ROLES.LIFEGUARD,
          ],
          roleContext: 'payment',
        },
      },
      // 送出後畫面 —— 紙本憑證要交給總務的那一頁（單號 + 應交清單）。同一批人：
      // 能建請款單的人才會落在這裡，而且頁面只認自己的單（CF 只回傳本人的請款單）。
      {
        path: 'payment/requests/:id/submitted',
        component: () => import('../pages/payment/RequestSubmittedPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [
            USER_ROLES.ADMIN,
            USER_ROLES.SUPERVISOR,
            USER_ROLES.TEACHER,
            USER_ROLES.TREASURY,
            USER_ROLES.STAFF,
            USER_ROLES.WORK_STUDY,
            USER_ROLES.LIFEGUARD,
          ],
          roleContext: 'payment',
        },
      },
      // 出差旅費 — own form (per-diem lines are server-derived, so the generic
      // RequestFormPage cannot host it). Roles READ the single source of truth the
      // CFs and the 差旅費 radio also read — never a second copy of the list.
      {
        path: 'payment/travel/new',
        component: () => import('../pages/payment/TravelRequestFormPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [...FRONTEND_BUSINESS_RULES.PAYMENT.REQUEST_CREATOR_ROLES],
          roleContext: 'payment',
        },
      },
      {
        path: 'payment/travel/:id/edit',
        component: () => import('../pages/payment/TravelRequestFormPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [...FRONTEND_BUSINESS_RULES.PAYMENT.REQUEST_CREATOR_ROLES],
          roleContext: 'payment',
        },
      },
      {
        path: 'payment/approval',
        component: () => import('../pages/payment/ApprovalQueuePage.vue'),
        meta: { requiresAuth: true, allowedRoles: [USER_ROLES.SUPERVISOR], roleContext: 'payment' },
      },
      {
        path: 'payment/treasury',
        component: () => import('../pages/payment/TreasuryDashboardPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.SUPERVISOR, USER_ROLES.TREASURY],
          roleContext: 'payment',
        },
      },
      {
        path: 'payment/treasury/batch-pay',
        component: () => import('../pages/payment/BatchPayPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.SUPERVISOR, USER_ROLES.TREASURY],
          roleContext: 'payment',
        },
      },
      {
        path: 'payment/payees',
        component: () => import('../pages/payment/PayeeDirectoryPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.SUPERVISOR, USER_ROLES.TREASURY],
          roleContext: 'payment',
        },
      },
      // 紙本憑證（收件・追蹤・交件給事務所）—— 取代「發票收驗」。總務收紙本並交件、
      // 主管處理逾期（延長／結案）與機密那一包、出納唯讀（可重印送件單）。頁面內的按鈕再依
      // useRoleContext 的 capabilities 分（已收是「誰保管誰按」，不是看角色大小）。
      {
        path: 'payment/paper-receipts',
        component: () => import('../pages/payment/PaperReceiptsPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.GENERAL_AFFAIRS, USER_ROLES.SUPERVISOR, USER_ROLES.TREASURY],
          roleContext: 'payment',
        },
      },
      // 舊網址（書籤、首頁卡片、通知連結）一律導到新頁。
      {
        path: 'payment/invoice-verification',
        redirect: '/payment/paper-receipts',
      },
      {
        path: 'payment/supervisor-request',
        component: () => import('../pages/payment/SupervisorRequestPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.SUPERVISOR, USER_ROLES.TREASURY],
          roleContext: 'payment',
        },
      },
      {
        path: 'payment/refunds',
        component: () => import('../pages/payment/RefundManagementPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'payment',
        },
      },
      // Refund form routes (create + edit)
      {
        path: 'payment/refund/new',
        component: () => import('../pages/payment/RefundRequestPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
          roleContext: 'payment',
        },
      },
      {
        path: 'payment/refund/:id/edit',
        component: () => import('../pages/payment/RefundRequestPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.SUPERVISOR, USER_ROLES.ADMIN],
          roleContext: 'payment',
        },
      },
      {
        path: 'payment/settings',
        component: () => import('../pages/payment/PaymentSettingsPage.vue'),
        meta: { requiresAuth: true, allowedRoles: [USER_ROLES.SUPERVISOR], roleContext: 'payment' },
      },
      // 日支數額表 — 出差旅費 rate-table maintenance (匯入政府版本 / 檢視差異 / 寫入).
      // 主管 + 出納 (PO 2026-09-03): the same rule the four maintenance CFs enforce
      // (PAYMENT.TRAVEL_RATE_TABLE_MAINTAINER_ROLES) and the same capability the
      // sidebar entry and the page's action buttons read (canManageTravelRateTables).
      {
        path: 'payment/travel-rates',
        component: () => import('../pages/payment/TravelRateTablesPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.SUPERVISOR, USER_ROLES.TREASURY],
          roleContext: 'payment',
        },
      },
      {
        path: 'payment/requests/:id',
        component: () => import('../pages/payment/RequestDetailPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [
            USER_ROLES.SUPERVISOR,
            USER_ROLES.ADMIN,
            USER_ROLES.TEACHER,
            USER_ROLES.TREASURY,
            USER_ROLES.GENERAL_AFFAIRS,
            USER_ROLES.STAFF,
            USER_ROLES.WORK_STUDY,
            USER_ROLES.LIFEGUARD,
          ],
          roleContext: 'payment',
        },
      },
      // ─── 出勤管理 (roleContext: 'attendance') ─────────────────────────────
      {
        path: 'attendance/clock',
        component: () => import('../pages/attendance/ClockPage.vue'),
        meta: {
          requiresAuth: true,
          // Punch (打卡) route surface: all staff-type roles incl. teacher; kiosk
          // excluded. Actual 打卡 eligibility is gated server-side by the
          // per-employee attendanceEnabled toggle (PO 2026-06-11 — per-account,
          // not per-role). ClockPage renders a friendly disabled state when off.
          allowedRoles: [
            USER_ROLES.SUPERVISOR,
            USER_ROLES.ADMIN,
            USER_ROLES.TEACHER,
            USER_ROLES.TREASURY,
            USER_ROLES.GENERAL_AFFAIRS,
            USER_ROLES.STAFF,
            USER_ROLES.LIFEGUARD,
            USER_ROLES.WORK_STUDY,
          ],
          roleContext: 'attendance',
        },
      },
      {
        path: 'attendance/my',
        component: () => import('../pages/attendance/MyAttendancePage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [
            USER_ROLES.SUPERVISOR,
            USER_ROLES.ADMIN,
            USER_ROLES.TEACHER,
            USER_ROLES.TREASURY,
            USER_ROLES.GENERAL_AFFAIRS,
            USER_ROLES.STAFF,
            USER_ROLES.LIFEGUARD,
            USER_ROLES.WORK_STUDY,
          ],
          roleContext: 'attendance',
        },
      },
      {
        path: 'attendance/leave/new',
        component: () => import('../pages/attendance/LeaveRequestPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [
            USER_ROLES.SUPERVISOR,
            USER_ROLES.ADMIN,
            USER_ROLES.TEACHER,
            USER_ROLES.TREASURY,
            USER_ROLES.GENERAL_AFFAIRS,
            USER_ROLES.STAFF,
            USER_ROLES.LIFEGUARD,
            USER_ROLES.WORK_STUDY,
          ],
          roleContext: 'attendance',
        },
      },
      {
        path: 'attendance/review',
        component: () => import('../pages/attendance/ReviewPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.SUPERVISOR, USER_ROLES.TREASURY],
          roleContext: 'attendance',
        },
      },
      {
        // 排班 (Phase 2). Open to all attendance-enabled staff; the page self-gates —
        // management surface for canManageScheduling, else own/granted read-only view.
        path: 'attendance/schedule',
        component: () => import('../pages/attendance/SchedulePage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [
            USER_ROLES.SUPERVISOR,
            USER_ROLES.ADMIN,
            USER_ROLES.TEACHER,
            USER_ROLES.TREASURY,
            USER_ROLES.GENERAL_AFFAIRS,
            USER_ROLES.STAFF,
            USER_ROLES.LIFEGUARD,
            USER_ROLES.WORK_STUDY,
          ],
          roleContext: 'attendance',
        },
      },
      {
        // 加班 (Phase 2 OT). Open to all attendance-enabled staff (申請 form + my list);
        // the page additionally shows the supervisor 簽核 queue when canReviewAttendance.
        path: 'attendance/overtime',
        component: () => import('../pages/attendance/OvertimeRequestPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [
            USER_ROLES.SUPERVISOR,
            USER_ROLES.ADMIN,
            USER_ROLES.TEACHER,
            USER_ROLES.TREASURY,
            USER_ROLES.GENERAL_AFFAIRS,
            USER_ROLES.STAFF,
            USER_ROLES.LIFEGUARD,
            USER_ROLES.WORK_STUDY,
          ],
          roleContext: 'attendance',
        },
      },
      {
        // 國定假日 (Phase 2 M-C.3). SUPERVISOR-only (spec §2.3, 2026-07-06). Only the
        // supervisor maintains the year's holiday table (OT 日別 classification). Admin and
        // treasury no longer reach it: the holiday-driven OT day-typing treasury's attendance
        // export needs is computed server-side, so treasury loses nothing operationally. Edit
        // controls on the page are gated by canManageScheduling (now supervisor-only).
        path: 'attendance/holidays',
        component: () => import('../pages/attendance/NationalHolidaysPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.SUPERVISOR],
          roleContext: 'attendance',
        },
      },
      {
        // 薪酬福利設定 (三節獎金 etc.). VIEW-ONLY reference bundle. SUPERVISOR-only
        // (spec §2.3, 2026-07-06). Treasury no longer reaches it — the OT/三節/年終 figures
        // it pays are exported server-side, so treasury loses nothing operationally.
        // Matches canManageHrConfig (now supervisor-only visibility gate).
        path: 'attendance/hr-settings',
        component: () => import('../pages/attendance/HrConfigPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: [USER_ROLES.SUPERVISOR],
          roleContext: 'attendance',
        },
      },
    ],
    meta: { requiresAuth: true },
  },
  // ─── Teacher Mobile Pages (roleContext: 'teacher-mobile') ─────────────────
  // 救生員 (view-only roster, docs/LIFEGUARD_MOBILE_ACCESS_SPEC.md) reach 首頁 / 上課名單 /
  // 學生名單 only; 操作說明, 學生紀錄 and 回饋 stay closed to them. The backend enforces
  // what they see (whitelisted fields, 2-week cap).
  {
    path: '/teacher/mobile',
    component: () => import('pages/TeacherMobilePage.vue'),
    meta: {
      requiresAuth: true,
      allowedRoles: [USER_ROLES.TEACHER, USER_ROLES.SUPERVISOR, USER_ROLES.LIFEGUARD],
      roleContext: 'teacher-mobile',
    },
  },
  {
    path: '/teacher/home',
    component: () => import('pages/TeacherMobileHomePage.vue'),
    meta: {
      requiresAuth: true,
      allowedRoles: [USER_ROLES.TEACHER, USER_ROLES.SUPERVISOR, USER_ROLES.LIFEGUARD],
      roleContext: 'teacher-mobile',
    },
  },
  {
    path: '/teacher/instructions',
    component: () => import('pages/TeacherInstructionsPage.vue'),
    meta: {
      requiresAuth: true,
      allowedRoles: [USER_ROLES.TEACHER, USER_ROLES.SUPERVISOR],
      roleContext: 'teacher-mobile',
    },
  },
  {
    path: '/teacher/mobile/class/:classId',
    component: () => import('pages/Teach_Mobile_StudentDetail.vue'),
    meta: {
      requiresAuth: true,
      allowedRoles: [USER_ROLES.TEACHER, USER_ROLES.SUPERVISOR, USER_ROLES.LIFEGUARD],
      roleContext: 'teacher-mobile',
    },
  },
  {
    path: '/teacher/student-dashboard/:studentId',
    name: 'StudentDashboardMobile',
    component: () => import('pages/StudentDashboardMobilePage.vue'),
    meta: {
      requiresAuth: true,
      allowedRoles: [USER_ROLES.TEACHER, USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR],
      roleContext: 'teacher-mobile',
    },
  },
  {
    path: '/teacher/feedback',
    component: () => import('pages/TeacherMobileFeedbackPage.vue'),
    meta: { requiresAuth: true, allowedRoles: [USER_ROLES.TEACHER], roleContext: 'teacher-mobile' },
  },

  // Always leave this as last one,
  // but you can also remove it
  {
    path: '/:catchAll(.*)*',
    component: () => import('pages/ErrorNotFound.vue'),
  },
];

export default routes;
